import React, { useMemo, useRef } from 'react';

import moment, { Moment } from 'moment';
import classNames from 'classnames';
import { Button, CommonPickerMethods, DatePicker, DateRangeKeyword } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import styles from './day-picker.module.scss';

interface Props {
	value?: string;
	onChange?: (value?: string) => void;
	errorStatus?: boolean;
}

export const dayPickerDynamicOptions = [DateRangeKeyword.Today, DateRangeKeyword.Tomorrow];

export const pickerDynamics = [
	{
		value: DateRangeKeyword.Today,
		name: 'FILTER_BAR.TODAY'
	},
	{
		value: DateRangeKeyword.Tomorrow,
		name: 'FILTER_BAR.TOMORROW'
	}
];

const closePicker = (picker: CommonPickerMethods) => {
	picker.blur();
	picker.focus();
};

const DayPicker = ({ value, onChange, errorStatus }: Props) => {
	const pickerRef = useRef<CommonPickerMethods>();
	const { t } = useTranslation();
	const normalizedValue = useMemo(() => {
		switch (value) {
			case DateRangeKeyword.Today:
				return moment();
			case DateRangeKeyword.Tomorrow:
				return moment().add(1, 'day');
			default:
				return value ? moment(value) : null;
		}
	}, [value]);

	const onDateChange = (newDate: Moment | null) => {
		onChange?.(newDate?.format('YYYY-MM-DD') || undefined);
	};

	const goToPrev = () => {
		onChange?.(normalizedValue.subtract(1, 'day').format('YYYY-MM-DD'));
	};

	const goToNext = () => {
		onChange?.(normalizedValue.add(1, 'day').format('YYYY-MM-DD'));
	};

	return (
		<div className={styles.dayPicker}>
			<DatePicker
				ref={pickerRef}
				data-test-id="monitoring-day-picker"
				format={'D MMM YYYY'}
				placeholder={t('FILTER_BAR.DATE')}
				onChange={onDateChange}
				value={normalizedValue}
				status={errorStatus ? 'error' : undefined}
				allowClear={false}
				showToday={false}
				renderExtraFooter={() => (
					<div className={styles.pickerFooter}>
						{pickerDynamics.map(dynamic => (
							<Button
								key={dynamic.value}
								type="link"
								onClick={() => {
									value !== dynamic.value && onChange?.(dynamic.value);
									if (pickerRef.current) {
										closePicker(pickerRef.current);
									}
								}}
								className={classNames({ [styles.selectedButton]: value === dynamic.value })}
							>
								{value === dynamic.value && <BringgIcon iconName={BringgFontIcons.Selected} />}
								{t(dynamic.name)}
							</Button>
						))}
					</div>
				)}
			/>
			<Button
				icon={<BringgIcon iconName={BringgFontIcons.ChevronLeft} />}
				data-test-id="prev-day-button"
				onClick={goToPrev}
				shape="circle"
				type="text"
			/>
			<Button
				icon={<BringgIcon iconName={BringgFontIcons.ChevronRight} />}
				onClick={goToNext}
				shape="circle"
				type="text"
				data-test-id="next-day-button"
			/>
		</div>
	);
};

export default DayPicker;
